import React, {useEffect, useState, useContext} from "react";
import {Divider} from 'antd';
import axios from "@common/axios";
import {UserContext} from "@context/UserContext";
import Button from "@common/Button";
import GenericDialog from "@common/GenericDialog";
import PageHeader from "@common/PageHeader";
import Tooltip from "@common/Tooltip";
import TableAntD from "@common/TableAntD";
import PopConfirm from "@common/PopConfirm";
import {isCancel} from "axios";
import {DeleteRounded, DraftsRounded, EmailRounded} from "@mui/icons-material";

const Announcements = props => {
    const {user} = useContext(UserContext);
    const [isDeleteAllDisabled, setDeleteAllDisabled] = useState(true);
    const [currentMessage, setCurrentMessage] = useState(null);
    const [isMarkCurrentMessageRead, setMarkCurrentMessageRead] = useState(false);
    const [isReadMessageDialogOpen, setReadMessageDialogOpen] = useState(false);
    const [reload, setReload] = useState(null);

    const INBOX_COLS = [
        {
            title: <span className={'ch-accessibility-label'}>Read status</span>,
            dataIndex: 'status',
            width: 36,
            render: (_, message) => (
                <Tooltip title={message.read ? 'Read' : 'Unread'}>
                    <span aria-hidden={true} style={{display: 'flex', alignItems: 'center', color: '#434343'}}>
                        {message.read ? <DraftsRounded /> : <EmailRounded />}
                    </span>
                </Tooltip>
            )
        },
        {title: 'Subject', dataIndex: 'subject', render: (_, message) => (
            <Button type={'text'} onClick={() => handleViewMessage(message)} aria-label={`${message.read ? 'Read' : 'Unread'} message: ${message.subject}`}>{message.subject}</Button>
        )},
        {title: 'From', dataIndex: 'from'},
        {title: 'Sent', dataIndex: 'sent'},
        {title: 'Expiry', dataIndex: 'expiry'},
        {
            title: 'Actions',
            dataIndex: 'actions',
            render: (_, message) => (
                <PopConfirm title={<span>Delete this message?</span>}
                            placement={'left'}
                            okText={'Delete'}
                            onConfirm={() => deleteMessage(message)}>
                    <>
                        <Tooltip title={'Delete'}>
                            <Button type={'text'} aria-label={'Delete message'}>
                                <DeleteRounded />
                            </Button>
                        </Tooltip>
                    </>
                </PopConfirm>
            )
        }
    ];

    useEffect(() => {
        setReadMessageDialogOpen(currentMessage !== null);
        if (currentMessage && !currentMessage.read) {
            axios.post('/web/ui/message/markUserMessageRead', null, {params: {messageId: currentMessage.key}})
                .then(() => {
                    setMarkCurrentMessageRead(true);
                }).catch(error => {
                console.error('error marking message as read', error);
            });
        }
    }, [currentMessage]);

    const updateReload = (doReload) => {
        setCurrentMessage(null);
        if (isMarkCurrentMessageRead || doReload) {
            setReload(new Date().getTime());
            setMarkCurrentMessageRead(false);
        }
    };

    const getUserMessages = (params, handleTableData, handlePagingData, abortController) => {
        axios.get('/web/ui/message/userMessagesInbox', {params: params, signal: abortController.signal})
            .then(response => {
                if (response && response.data) {
                    let dataSource = [];

                    response.data.currPageContents.map((message) => {
                        dataSource.push({
                            key: message.messageId,
                            to: message.recipientName,
                            from: message.senderName,
                            subject: message.subject,
                            sent: user.dateFormat.format(message.createDate),
                            expiry: user.dateFormat.format(message.expiryDate),
                            read: message.read,
                            body: message.messageBody
                        });
                    });

                    handleTableData(dataSource);
                    handlePagingData({pageSize: params.pageSize, totalResults: response.data.totalResults, currPage: params.pageNumber});
                    setDeleteAllDisabled(response.data.totalResults == 0);
                }
            })
            .catch((error) => {
                if (!isCancel(error)) {
                    console.error('Error retrieving announcements: ', error);
                    displayToastMessage('Error retrieving announcements:', error, 'error');
                }
            })
            .finally(() => {
                abortController = null;
            });
    };

    const deleteMessage = (message) => {
        const markRead = message.read
            ? Promise.resolve()
            : axios.post('/web/ui/message/markUserMessageRead', null, {params: {messageId: message.key}});

        markRead
            .then(() => axios.post('/web/ui/message/deleteUserMessages', [message.key]))
            .then(response => {
                if (response.data) {
                    setReload(new Date().getTime());
                    displayToastMessage('Announcements', 'Announcement deleted successfully', 'success', undefined, 5000);
                }
            }).catch(error => {
            console.error('error deleting announcement', error);
        });
    };

    const deleteAllMessages = () => {
        axios.post('/web/ui/message/deleteAllUserMessages')
            .then(response => {
                if (response.data) {
                    updateReload(true);
                    displayToastMessage('Announcements', 'All announcements deleted successfully', 'success', undefined, 5000);
                }
            }).catch(error => {
            console.error('error deleting announcement', error);
        });
    };

    const handleViewMessage = (message) => {
        setCurrentMessage(message);
    };

    return (
        <div className={'ch-messages-wrapper'}>
            <PageHeader
                title={'Inbox'}
                buttons={
                    <PopConfirm title={<span>Delete all announcements?</span>}
                                placement={'top'}
                                okText={'Delete All'}
                                onConfirm={deleteAllMessages}>
                        <Button type={'outline'} disabled={isDeleteAllDisabled} aria-label={'Delete all announcements'}>Delete All</Button>
                    </PopConfirm>
                }
            />
            <TableAntD
                scroll={{x: 'max-content'}}
                size={'small'}
                reload={reload}
                columnData={INBOX_COLS}
                pagingData={{fetchFunction: getUserMessages, params: {mailbox: 'INBOX', pageSize: 10, currentPage: 1}}}
                noDataMessage={'There are no announcements in your inbox'}
                rowClassName={(record) => (record.read ? '' : 'ch-messages-unread')}
            />

            <GenericDialog
                title={currentMessage ? currentMessage.subject : 'View message'}
                open={isReadMessageDialogOpen}
                onClose={updateReload}
                buttons={[{name: 'Close'}]}
                dialogWidth={820}
            >
                {currentMessage &&
                    <>
                        <div className={'ch-text-b ch-text-large'}>{currentMessage.from}</div>
                        <Divider className={'margin-t--m margin-b--s'} />
                        <div dangerouslySetInnerHTML={{__html: currentMessage.body}} />
                    </>
                }
            </GenericDialog>
        </div>
    );
};

export default Announcements;
